<script setup lang="ts">
import { ref } from "vue";
import { useDebounceFn } from "@vueuse/core";
import * as Highcharts from "highcharts";
import ImpactLoading from "./ImpactLoading.vue";
import ImpactError from "./ImpactError.vue";
import ImpactNoData from "./ImpactNoData.vue";

interface IImpactTopProps {
  items: Array<Highcharts.PointOptionsObject>;
  isLoading: boolean;
  hasError: boolean;
}
const props = defineProps<IImpactTopProps>();
const emit = defineEmits(["error"]);
const chartEl = ref<HTMLElement | null>(null);
const chart = ref<Highcharts.Chart | null>(null);

const higherPoint = computed(() => {
  if (props.items.length === 0) {
    return 0;
  }
  const points = props.items.map((item) => item.y);
  if (points.includes(undefined)) {
    return 0;
  }
  return Math.max(...(points as number[]));
});

const serieTop = computed(() => {
  return props.items.map((item) => item.y);
});

const serieMax = computed(() => {
  return props.items.map((item) => {
    return {
      y: higherPoint.value,
      label: item.y
    };
  });
});

const categories = computed(() => {
  return props.items.map((item) => item.name);
});

const renderChart = () => {
  chart.value = Highcharts.chart({
    chart: {
      renderTo: chartEl.value as HTMLElement,
      type: "bar",
      animation: true,
      spacingTop: 0,
      spacingRight: 0,
      spacingBottom: 0,
      spacingLeft: 0,
      plotBorderWidth: 0,
      margin: [0, 0, 0, 0],
      backgroundColor: "transparent",
      style: {
        fontFamily: "var(--font-museo)"
      }
    },
    tooltip: {
      enabled: false
    },
    legend: {
      enabled: false
    },
    credits: {
      enabled: false
    },
    plotOptions: {
      series: {
        marker: {
          enabled: false
        },
        pointPlacement: -0.2,
        dataLabels: {
          useHTML: true,
          inside: false,
          y: -21,
          x: 0,
          style: {
            minWidth: "50px",
            backgroundColor: "var(--grey60)",
            color: "var(--black)",
            fontWeight: "500",
            fontSize: "12px",
            textOutline: "none",
            textAlign: "right"
          }
        }
      },
      bar: {
        grouping: false,
        shadow: false,
        borderWidth: 0
      }
    },
    xAxis: {
      categories: categories.value,
      tickWidth: 0,
      title: {
        text: null
      },
      lineWidth: 0,
      labels: {
        align: "left",
        x: 0,
        y: -7,
        style: {
          whiteSpace: "nowrap",
          color: "var(--grey70)",
          fontWeight: "300"
        }
      }
    },
    yAxis: {
      max: higherPoint.value,
      labels: {
        enabled: false
      },
      gridLineWidth: 0,
      minorGridLineWidth: 0,
      visible: false
    },
    title: {
      text: ""
    },
    series: [
      {
        type: "bar",
        name: "total",
        color: "var(--grey20)",
        states: {
          hover: {
            enabled: false
          }
        },
        enableMouseTracking: false,
        dataLabels: {
          enabled: true,
          formatter: function () {
            return `<div style="min-width: 68px;">${this.point.label.toLocaleString(
              "en-US"
            )}</div>`;
          }
        },
        data: serieMax.value
      },
      {
        type: "bar",
        name: "top",
        color: "var(--maincolor50)",
        states: {
          hover: {
            enabled: false
          }
        },
        enableMouseTracking: false,
        dataLabels: {
          enabled: false
        },
        data: serieTop.value
      }
    ]
  });
};

const resizeDebouncedFn = useDebounceFn(() => {
  if (chart.value) {
    renderChart();
  }
}, 300);

const impactTopOnError = () => {
  emit("error");
};

const noDataAvailable = computed(() => {
  return props.items.length === 0 && !props.isLoading && !props.hasError;
});

watch(
  () => props.items,
  () => {
    if (chartEl.value && props.items.length > 0) {
      renderChart();
    }
  }
);

onMounted(() => {
  if (chartEl.value && props.items.length > 0) {
    renderChart();
  }
  window.addEventListener("resize", resizeDebouncedFn);
});
onUnmounted(() => {
  window.removeEventListener("resize", resizeDebouncedFn);
});
</script>

<template>
  <ImpactLoading v-if="isLoading" />
  <ImpactError v-else-if="hasError" @error="impactTopOnError" />
  <ImpactNoData v-else-if="noDataAvailable" />
  <div
    v-else
    ref="chartEl"
    class="ImpactTop"
    :style="{ height: `${props.items.length * 54}px` }"></div>
</template>
